import React, { useState } from 'react';
import ServiceButton from './ServiceButton';
import { 
  ToiletIcon, 
  ShowerIcon, 
  BathtubIcon, 
  SinkIcon, 
  LavaboIcon,
  WaterHeaterIcon, 
  HeatingIcon,
  LeakIcon,
  ToolsIcon,
  OtherIcon
} from './PlumbingIcons';

interface ServiceGridProps {
  serviceType: 'installation' | 'reparation';
  onServiceSelect: (service: string) => void;
  onContactClick?: () => void;
}

// Définition des prix approximatifs par service et type
const servicePrices: Record<string, Record<string, string | number>> = {
  installation: {
    "Ballon d'eau": "400€ à 1500€",
    "Douche": "Sur devis",
    "Baignoire": "Sur devis",
    "Évier": "Sur devis",
    "Toilette": "À partir de 220€",
    "Lavabo": "À partir de 150€",
    "Sanibroyeur": "À partir de 300€",
    "Chauffage": "Sur devis",
    "Autre": "Sur devis"
  },
  reparation: {
    "Ballon d'eau": "À partir de 120€",
    "Douche": "À partir de 90€",
    "Baignoire": "À partir de 90€",
    "Évier": "À partir de 80€",
    "Toilette": "À partir de 90€",
    "Lavabo": "À partir de 80€",
    "Sanibroyeur": "À partir de 110€",
    "Chauffage": "Sur devis",
    "Autre": "Sur devis"
  }
};

const ServiceGrid: React.FC<ServiceGridProps> = ({ serviceType, onServiceSelect, onContactClick }) => {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const handleServiceClick = (service: string) => {
    setSelectedService(service);
    // Appeler la fonction de callback du parent pour traiter la sélection
    onServiceSelect(service);
    
    // Dans une version future, cette fonction pourrait naviguer vers une page de détails
    // ou ouvrir un modal avec plus d'informations sur le service
    console.log(`Service sélectionné: ${service} (${serviceType})`);
  };

  const handleContactClick = () => {
    if (onContactClick) {
      onContactClick();
    }
  };

  return (
    <div className="p-6 bg-white rounded-2xl shadow-md transition-all duration-500 animate-fadeIn">
      <h2 className="text-xl font-semibold text-secondary mb-4 text-center">
        {serviceType === 'installation' ? 'Installation' : 'Réparation'} <span className="text-primary">à effectuer</span>
      </h2>
      
      <div className="grid grid-cols-3 gap-4 md:gap-6">
        <ServiceButton 
          icon={<WaterHeaterIcon />} 
          label="Chauffe eau" 
          onClick={() => handleServiceClick("Chauffe eau")} 
          selected={selectedService === "Chauffe eau"}
        />
        <ServiceButton 
          icon={<ShowerIcon />} 
          label="Douche" 
          onClick={() => handleServiceClick("Douche")} 
          selected={selectedService === "Douche"}
        />
        <ServiceButton 
          icon={<BathtubIcon />} 
          label="Baignoire" 
          onClick={() => handleServiceClick("Baignoire")} 
          selected={selectedService === "Baignoire"}
        />
        
        <ServiceButton 
          icon={<SinkIcon />} 
          label="Évier" 
          onClick={() => handleServiceClick("Évier")} 
          selected={selectedService === "Évier"}
        />
        <ServiceButton 
          icon={<ToiletIcon />} 
          label="Toilette" 
          onClick={() => handleServiceClick("Toilette")} 
          selected={selectedService === "Toilette"}
        />
        <ServiceButton 
          icon={<LavaboIcon />} 
          label="Lavabo" 
          onClick={() => handleServiceClick("Lavabo")} 
          selected={selectedService === "Lavabo"}
        />
        
        <ServiceButton 
          icon={<LeakIcon />} 
          label="Sanibroyeur" 
          onClick={() => handleServiceClick("Sanibroyeur")} 
          selected={selectedService === "Sanibroyeur"}
        />
        <ServiceButton 
          icon={<HeatingIcon />} 
          label="Chauffage" 
          onClick={() => handleServiceClick("Chauffage")} 
          selected={selectedService === "Chauffage"}
        />
        <ServiceButton 
          icon={<OtherIcon />} 
          label="Autre" 
          onClick={() => handleServiceClick("Autre")} 
          selected={selectedService === "Autre"}
        />
      </div>

      {selectedService && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fadeIn">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-medium text-secondary">Service sélectionné:</h3>
              <p className="text-lg font-semibold">{selectedService}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Prix estimé:</p>
              <p className="text-lg font-bold text-primary">
                {servicePrices[serviceType][selectedService]}
              </p>
            </div>
          </div>
          <button 
            onClick={handleContactClick}
            className="mt-4 w-full py-2 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors duration-300 flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Contacter un plombier
          </button>
        </div>
      )}
    </div>
  );
};

export default ServiceGrid; 